import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { MatrixService } from '../matrix/matrix.service';
import { AppConfigService } from '../config/app-config.service';
import { Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { MatrixClient } from 'matrix-js-sdk';
import { ClientWithOrigin } from '../../model/client-with-origin';

interface TimAuthResponse {
  access_token?: string;
  token?: string;
  expires_in?: number;
}

function stripTrailingSlash(value: string): string {
  if (!value) return '';

  while (value.endsWith('/')) {
    value = value.slice(0, -1);
  }

  return value;
}

function stripEndingTimAuthenticate(value: string): string {
  return (value || '').replace(/\/tim-authenticate\/?$/i, '');
}

function toBool(v: string | null | undefined, fallback = false): boolean {
  if (typeof v !== 'string') return fallback;
  return v.trim().toLowerCase() === 'true';
}

@Injectable({
  providedIn: 'root'
})
/**
 * Die VzdTokenService-Klasse ist ein zentraler Angular-Service, der für die Beschaffung,
 * Verwaltung und Zwischenspeicherung von Zugriffs-Token für das VZD-FHIR-Verzeichnis verantwortlich ist.
 * A_25479 - Search Token
 */
export class VzdTokenService {
  protected readonly config = inject(AppConfigService);
  private cachedSearchAccessToken: { token: string; expiresAtEpochMs: number } | null = null;
  private readonly userAgentHeader: string;

  private readonly absoluteAuthEndpointUrl: string;
  private readonly encodeMxid: boolean;
  private readonly vzdFhirUrl: string;
  private readonly regDienstUrl = this.config.get('regDienstUrl');
  private readonly vzdBase = this.config.get('vzdFhirUrl');
  private cachedOwnerToken: { token: string; expiresAt: number } | null = null;

  private readonly httpClient = inject(HttpClient);
  private readonly matrixService = inject(MatrixService);
  private readonly router = inject(Router);

  constructor() {
    this.vzdFhirUrl = this.config.get('vzdFhirUrl');
    this.encodeMxid = toBool(this.config.get('encodeMxid'));
    const backendRoot = stripTrailingSlash(stripEndingTimAuthenticate(this.vzdFhirUrl));
    this.absoluteAuthEndpointUrl = `${backendRoot}/tim-authenticate`;
    this.userAgentHeader = navigator.userAgent;
  }

  public async getToken(): Promise<string> {
    if (this.router.url.includes('org-admin-client')) {
      return this.getOwnerAccessToken();
    } else {
      return this.acquireSearchAccessToken();
    }
  }

  /**
   * Access-Token für VZD-Suche über /tim-authenticate beschaffen (Mock oder Real).
   * Auth-URL: `${absoluteAuthEndpointUrl}?mxId=<MXID>`
   */
  private async acquireSearchAccessToken(): Promise<string> {
    const nowEpochMs = Date.now();

    if (this.cachedSearchAccessToken && this.cachedSearchAccessToken.expiresAtEpochMs - 60_000 > nowEpochMs) {
      return this.cachedSearchAccessToken.token;
    }

    // OpenID-Token aus Matrix
    const openIdAccessToken = await this.acquireOpenIdAccessTokenFromMatrix();

    // Homeserveradresse holen
    const rawServeruri = this.config.get('homeserverHostname');

    const serverId = this.encodeMxid ? encodeURIComponent(rawServeruri) : rawServeruri;

    const authUrl = `${this.absoluteAuthEndpointUrl}?mxId=${serverId}`;
    const headers = new HttpHeaders({
      'X-Matrix-OpenID-Token': openIdAccessToken,
      'X-TIM-User-Agent': this.userAgentHeader,
      Accept: 'application/json'
    });

    try {
      const resp = await firstValueFrom(
        this.httpClient.get<TimAuthResponse>(authUrl, {
          headers,
          observe: 'response',
          withCredentials: false
        })
      );

      const body: TimAuthResponse = resp.body ?? {};
      const token = body.access_token ?? body.token;
      if (!token) {
        throw new Error('tim-authenticate returned no token');
      }

      const expMs = this.determineExpiryEpochMsFromToken(nowEpochMs, token, body.expires_in);
      this.cachedSearchAccessToken = { token, expiresAtEpochMs: expMs };
      return token;
    } catch (err: unknown) {
      if (err instanceof HttpErrorResponse) {
        throw new TypeError(`tim-authenticate failed: ${err.status} ${err.message}`.trim());
      }
      throw err ?? new Error('tim-authenticate failed: unknown error');
    }
  }

  /** OpenID-Token vom Matrix-Client besorgen */
  private async acquireOpenIdAccessTokenFromMatrix(): Promise<string> {
    let matrixClient = this.matrixService.getClientSync?.();
    matrixClient ??= await this.waitForMatrixClient();
    if (!matrixClient) {
      throw new Error('No active Matrixclient');
    }

    const response = await matrixClient.getOpenIdToken();
    const token: string | null = typeof response === 'string' ? response : (response.access_token ?? null);
    if (!token) throw new Error('No OpenID access_token in response');
    return token;
  }

  private async waitForMatrixClient(): Promise<MatrixClient | null> {
    try {
      const client$ = this.matrixService.getClientWithOrigin$?.() as Observable<ClientWithOrigin> | undefined;
      if (!client$?.subscribe) return null;

      return await new Promise<MatrixClient | null>(resolve => {
        const sub = client$.subscribe(withOrigin => {
          const client = withOrigin?.client ?? null;
          if (client) {
            sub.unsubscribe();
            resolve(client);
          }
        });
      });
    } catch {
      return null;
    }
  }

  private determineExpiryEpochMsFromToken(nowEpochMs: number, token: string, expiresInSeconds?: number): number {
    if (typeof expiresInSeconds === 'number') {
      return nowEpochMs + expiresInSeconds * 1000;
    }
    const parts = token.split('.');
    if (parts.length === 3) {
      try {
        const payload = JSON.parse(atob(parts[1])) as { exp?: number };
        if (typeof payload.exp === 'number') return payload.exp * 1000;
      } catch {
        /* ignore */
      }
    }
    return nowEpochMs + 5 * 60_000;
  }

  /**
   * Public intern (Service-intern nutzbar in allen Schreib-Operationen):
   * Holt owner_accesstoken
   */
  public async getOwnerAccessToken(): Promise<string> {
    const now = Date.now();

    if (this.cachedOwnerToken && this.cachedOwnerToken.expiresAt > now) {
      console.info('[OrgAdminFhirHealthcareServiceService] Using cached owner_accesstoken', {
        validUntil: new Date(this.cachedOwnerToken.expiresAt).toISOString()
      });
      return this.cachedOwnerToken.token;
    }

    const regToken = await this.fetchRegServiceToken();
    const ownerToken = await this.exchangeForOwnerAccessToken(regToken);

    this.cachedOwnerToken = {
      token: ownerToken,
      expiresAt: now + 23 * 60 * 60 * 1000
    };

    console.info('[OrgAdminFhirHealthcareServiceService] owner_accesstoken ready for write operations', {
      note: 'Use this as Bearer on VZD-FHIR-Directory writes',
      validForHours: 24
    });

    return ownerToken;
  }

  /**
   * * Creates new healthcare-service and returns the resource of the created healthcareService
   *    * @param fhirHealthcareService
   */
  private async fetchRegServiceToken(): Promise<string> {
    const url = `${this.regDienstUrl}/regservicetoken`;

    console.info('[OrgAdminFhirHealthcareServiceService] get RegService token', {
      method: 'GET',
      url
    });

    const response = await firstValueFrom(
      this.httpClient.get<{ returnCode: string; vorgangsId: string; idtoken: string }>(url)
    );

    const regToken = response.idtoken;

    if (!regToken) {
      throw new Error('RegService token empty (Step 1)');
    }
    return regToken;
  }

  /**
   * RegService-openId-Token gegen owner_accesstoken tauschen
   * GET /owner-authenticate am VZD-FHIR-Directory
   */
  private async exchangeForOwnerAccessToken(regToken: string): Promise<string> {
    const url = `${this.vzdBase}/owner-authenticate`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${regToken}`
    });
    const ownerToken = await firstValueFrom(this.httpClient.get<TimAuthResponse>(url, { headers }));
    if (!ownerToken) {
      throw new Error('owner_accesstoken empty (Step 2)');
    }
    if (!ownerToken.access_token) {
      throw new Error('no token in Response');
    }

    return ownerToken.access_token;
  }
}
